import React, { FC } from 'react'

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSubContent,
} from '../../../../ui/dropdown-menu'
import { IconMagneticCard } from '../../../../icons/IconMagneticCard'
import { IconPlus } from '../../../../icons/IconPlus'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip'
import { SendRequest } from '../PaymentActions/components/SendRequest/SendRequest'
import { RemoveCard } from '../PaymentActions/components/RemoveCard/RemoveCard'
import { PendingRequest } from '../PaymentActions/components/PendingRequest/PendingRequest'
import { RejectedRequest } from '../PaymentActions/components/RejectedRequest/RejectedRequest'

import { usePaymentActions } from '../../../../../hooks/usePaymentActions'
import { getPaymentRequestStatus } from '../../../../../helpers/payment'
import { cn } from '../../../../../lib/utils'

import { useAppSelector } from '../../../../../store/hooks'
import { selectProfileData } from '../../../../../store/user/userSlice'

import {
  IPaymentsCards,
  ISelectedCard,
  IUserInterface,
} from '../../../../../models/users/user.interface'

interface CartPaymentProps {
  setLoading: (loading: boolean) => void
  organizer: IUserInterface | null
  user: IUserInterface | null
  paymentsCards: IPaymentsCards
  setPaymentsCards: (cards: IPaymentsCards) => void
  onPayByCardAction: (card: ISelectedCard) => Promise<void>
}
export const CartPayment: FC<CartPaymentProps> = ({
  paymentsCards,
  setPaymentsCards,
  setLoading,
  onPayByCardAction,
  organizer,
  user,
}) => {
  const { onAddNewCard } = usePaymentActions()

  const profileData = useAppSelector(selectProfileData)

  return (
    <DropdownMenuPortal>
      <DropdownMenuSubContent className="w-[300px] direction-ltr">
        <DropdownMenuGroup>
          {paymentsCards.CARDS.map((card, index) => {
            let paymentRequestStatus: 'approved' | 'pending' | 'rejected' | null

            if (
              card.customerId === organizer?.userStripeId ||
              card.customerId === profileData?.userStripeId
            ) {
              paymentRequestStatus = 'approved'
            } else {
              paymentRequestStatus =
                card.pmId && organizer?.id && user
                  ? getPaymentRequestStatus(card.pmId, organizer?.id, user)
                  : null
            }

            return (
              <DropdownMenuItem key={index} className="capitalize">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        paymentRequestStatus === 'approved'
                          ? 'cursor-pointer hover:underline'
                          : 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={async () => {
                        if (paymentRequestStatus === 'approved') {
                          setLoading(true)
                          await onPayByCardAction({
                            type: card.type,
                            pmId: card.pmId,
                            cardNumber: card.cardNumber,
                          })
                          setLoading(false)
                        }
                      }}
                    >
                      <IconMagneticCard className="w-4" />
                      {card.cardType} ••••{card.cardNumber}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent hidden={paymentRequestStatus === 'approved'}>
                    You don't have access please send request
                  </TooltipContent>
                </Tooltip>
                <div className="ml-auto flex items-center gap-2">
                  {user?.userStripeDefaultPmId === card.pmId && (
                    <span className="leading-none text-[10px] py-1 px-2 border border-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor rounded-full">
                      Default
                    </span>
                  )}
                  {paymentRequestStatus !== 'pending' &&
                    paymentRequestStatus !== 'approved' &&
                    paymentRequestStatus !== 'rejected' && (
                      <SendRequest user={user} card={card} organizer={organizer} />
                    )}
                  {paymentRequestStatus === 'pending' && <PendingRequest user={user} />}
                  {paymentRequestStatus === 'rejected' && <RejectedRequest user={user} />}
                  <RemoveCard
                    card={card}
                    organizer={organizer}
                    setPaymentsCards={setPaymentsCards}
                    paymentsCards={paymentsCards}
                  />
                </div>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuGroup>
        <DropdownMenuItem
          onClick={() => {
            setLoading(true)
            onAddNewCard({
              setPaymentsCards: setPaymentsCards,
              user: user,
              organizer: organizer,
            })
            setLoading(false)
          }}
          className="cursor-pointer hover:underline"
        >
          <IconPlus className="w-[12px]" strokeWidth={2} color="black" />
          Add new card
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuPortal>
  )
}
